import React, { useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaLeaf } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const Counter = ({ from, to, duration = 1.5, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(from);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView) {
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
        
        // Easing function (easeOutQuart)
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * (to - from) + from));
        
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          setCount(to); // Ensure exactly target value at end
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isInView, from, to, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

const statsData = [
  { label: 'Licensed Since', value: 2021, prefix: '' },
  { label: 'Google Rating', value: 5, suffix: '.0★' },
  { label: 'Specialist', value: 'Automatic-Only', type: 'text' },
  { label: 'Service Area', value: 'Goulburn', type: 'text' },
];

export default function Stats() {
  return (
    <section className="bg-brand-navy py-12 relative border-b-4 border-brand-gold z-20">
      {/* Decorative Laurel Left */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-20 hidden md:block">
        <SafeIcon icon={FaLeaf} className="text-brand-gold w-16 h-16 -scale-x-100" />
      </div>

      <div className="container mx-auto px-2 sm:px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-2 sm:gap-4 divide-x-0 md:divide-x-2 divide-white/10">
          {statsData.map((stat, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center px-1 sm:px-4"
            >
              <div className="font-display text-[1.4rem] min-[375px]:text-2xl sm:text-3xl md:text-4xl text-brand-gold mb-2 drop-shadow-md tracking-tight sm:tracking-normal whitespace-nowrap">
                {stat.type === 'text' ? (
                  stat.value
                ) : (
                  <Counter from={0} to={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
                )}
              </div>
              <div className="text-white/80 font-medium uppercase tracking-wider text-[0.65rem] min-[375px]:text-xs sm:text-sm">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Decorative Laurel Right */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-20 hidden md:block">
        <SafeIcon icon={FaLeaf} className="text-brand-gold w-16 h-16" />
      </div>
    </section>
  );
}